import { ElementRef, Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartsUtils } from '@shared/components/charts/charts.utils';
import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';
import { BehaviorSubject, debounceTime, filter, Observable, Subject, takeUntil, tap } from 'rxjs';
import { BlockViewScaleByFactor } from '../models/catalog-plan-constants';
import { CanvasProps, CatalogPlanDetailsTooltip, SelectionType } from '../models/catalog-plan.model';
import { CatalogPlanBlockViewMinimapService } from './catalog-plan-blockView-minimap.service';
import { CatalogPlanBlockViewUtilsService } from './catalog-plan-blockView-utils.service';
import { CatalogPlanBlockViewCommunicationService } from './catalog-plan-blockview-communication.service';

@Injectable({
  providedIn: 'root',
})
export class CatalogPlanBlockViewInteractionService {
  private readonly utils = inject(CatalogPlanBlockViewUtilsService);
  private readonly minimap = inject(CatalogPlanBlockViewMinimapService);
  private readonly communicationService = inject(CatalogPlanBlockViewCommunicationService);
  private readonly translateService = inject(TranslateService);

  private stage: Konva.Stage;
  private selectionLayer: Konva.Layer;
  private contingentLayer: Konva.Layer;

  private _selectionMode: SelectionType;
  // can't cast it to NodeJS.Timeout so im keeping it to any
  private selectionShapesTimeoutId: any;
  private selectionRect: Konva.Rect;
  private selectionLasso: Konva.Line;
  private isLassoDrawingInProgress = false;
  private _isPlanDrawingInProgress = false;
  isShowMiniMap: boolean;
  private selectionRectPoints: { x1: number; x2: number; y1: number; y2: number } = {
    x1: 0,
    x2: 0,
    y1: 0,
    y2: 0,
  };

  private container: ElementRef;
  private _initialScale: number;
  private _maxScaleValue: number;
  private canvasProps: CanvasProps;

  private _currentScale$ = new BehaviorSubject<number>(1);
  private destroy$ = new Subject<void>();
  private selectionTooltip: Konva.Label;
  private selectionTooltipChanged$ = new Subject<void>();
  private readonly _seatDetailsRequested$ = new Subject<CatalogPlanDetailsTooltip>();

  public get isPlanDrawingInProgress() {
    return this._isPlanDrawingInProgress;
  }

  public set isPlanDrawingInProgress(value: boolean) {
    this._isPlanDrawingInProgress = value;
  }

  public get maxScaleValue() {
    return this._maxScaleValue;
  }

  public set maxScaleValue(value: number) {
    this._maxScaleValue = value;
  }

  public get seatDetailsRequested$(): Observable<CatalogPlanDetailsTooltip> {
    return this._seatDetailsRequested$.asObservable();
  }

  public get currentScale$() {
    return this._currentScale$;
  }

  public get selectionMode() {
    return this._selectionMode;
  }

  public get initialScale() {
    return this._initialScale;
  }

  public init(stage: Konva.Stage, container: ElementRef, canvasProps: CanvasProps, isShowMiniMap: boolean) {
    this.stage = stage;
    this.isShowMiniMap = isShowMiniMap;
    this.contingentLayer = this.stage.findOne(`#catalog-contingent-layer`);
    this.selectionLayer = this.stage.findOne(`#catalog-selection-layer`);
    this.container = container;
    this.canvasProps = canvasProps;
  }

  public addStageEventListeners() {
    if (this.isShowMiniMap) {
      this.followMinimapScaleRect();
    }
    this.addStageGlobalEventListeners();
    this.addSeatSelectionEventListeners();
  }

  private addStageGlobalEventListeners() {
    this.createSelectionRect();
    this.createSelectionTooltip();
    this.updateTooltipPositionAndValue();
    this.stage.on('wheel', (e) => {
      this.applyZoom(e, this.getDirection(e));
    });
    this.stage.on('mouseleave', () => {
      this.isLassoDrawingInProgress = false;
      this.resetLasso();
    });
    this.stage.on('dragend', () => {
      if (this.isShowMiniMap) this.minimap.updateScaleRectOnZoom(this.stage.scaleX(), this.stage.position());
    });
  }

  private getDirection(e) {
    let direction = e?.evt?.deltaY > 0 ? -1 : 1;
    if (e?.evt?.ctrlKey) {
      direction = -direction;
    }
    return direction;
  }

  private addSeatSelectionEventListeners() {
    this.createSelectionRect();
    let hoverTimer;
    this.stage.on('mouseover', (event: Konva.KonvaEventObject<MouseEvent>) => {
      hoverTimer = setTimeout(() => {
        this.onStageContextMenu(event);
      }, 1000);
    });
    this.stage.on('mouseout', () => {
      clearTimeout(hoverTimer);
    });
    this.stage.on('mousedown touchstart', (event: Konva.KonvaEventObject<MouseEvent>) =>
      this.onStageMousedown(event),
    );
    this.stage.on('mousemove touchmove', (event: Konva.KonvaEventObject<MouseEvent>) =>
      this.onStageMousemove(event),
    );
    this.stage.on('mouseup touchend', (event: Konva.KonvaEventObject<MouseEvent>) =>
      this.onStageMouseup(event),
    );
    this.contingentLayer.on('click', (event: Konva.KonvaEventObject<MouseEvent>) => this.onStageClick(event));
  }

  private onStageClick(event: Konva.KonvaEventObject<MouseEvent>) {
    if (this._selectionMode === SelectionType.Lasso) {
      return;
    }

    if (event?.evt?.button === 0) {
      const { node, data } = this.utils.getSeatByPosition(event.target);
      if (!this.utils.isSeatLocked(data) && this.utils.isSelectableShape(node)) {
        this.communicationService.updateSeatSelection([
          this.utils.selectOneSeat({
            node,
            data,
            seatId: data?.seatId,
          }),
        ]);
      }
    }
  }

  private onStageMouseup(event: Konva.KonvaEventObject<MouseEvent>) {
    event.evt.preventDefault();
    if (event.evt.which === 3) {
      this.stage.stopDrag();
      return;
    }
    if (!this.selectionRect.visible() && this._selectionMode === SelectionType.Default) {
      return;
    }
    // update visibility in timeout, so we can check it in click event
    switch (this._selectionMode) {
      case SelectionType.Default: {
        this.handleDefaultSelection();
        break;
      }
      case SelectionType.Lasso: {
        this.handleLassoSelection();
        break;
      }
    }
  }

  private handleDefaultSelection() {
    setTimeout(() => {
      this.resetSelectionTooltip();
      this.selectionRect.visible(false);
      const newSelection = this.selectSeatsBasedOnEventType(this.selectionRect);
      this.updateSeatSelection(newSelection);
    });
  }

  private handleLassoSelection() {
    this.resetSelectionTooltip();
    this.isLassoDrawingInProgress = false;
    this.selectionShapesTimeoutId = setTimeout(() => {
      const newSelection = this.selectSeatsBasedOnEventType(this.selectionLasso);
      this.resetLasso();
      this.updateSeatSelection(newSelection);
    }, 500);
  }

  private selectSeatsBasedOnEventType(selectionShape: any): any[] {
    return this.utils.selectSeats(this.contingentLayer, selectionShape);
  }
  private updateSeatSelection(newSelection: any[]) {
    if (newSelection?.length) {
      this.communicationService.updateSeatSelection(newSelection);
    }
  }

  private onStageMousemove(event: Konva.KonvaEventObject<MouseEvent>) {
    event.evt.preventDefault();
    const mousePointer = this.utils.getPointerPosition(this.stage);
    switch (this._selectionMode) {
      case SelectionType.Default: {
        if (!this.selectionRect.visible()) {
          break;
        }
        this.selectionRectPoints.x2 = mousePointer.x;
        this.selectionRectPoints.y2 = mousePointer.y;
        this.selectionRect.setAttrs({
          x: Math.min(this.selectionRectPoints.x1, this.selectionRectPoints.x2),
          y: Math.min(this.selectionRectPoints.y1, this.selectionRectPoints.y2),
          width: Math.abs(this.selectionRectPoints.x2 - this.selectionRectPoints.x1),
          height: Math.abs(this.selectionRectPoints.y2 - this.selectionRectPoints.y1),
        });
        this.displaySelectionTooltip(event?.evt);

        break;
      }
      case SelectionType.Lasso: {
        if (!this.isLassoDrawingInProgress) {
          break;
        }
        const newPoints = this.selectionLasso.points().concat([mousePointer.x, mousePointer.y]);
        this.selectionLasso.points(newPoints);
        this.displaySelectionTooltip(event?.evt);

        break;
      }
    }
  }

  private onStageMousedown(event: Konva.KonvaEventObject<MouseEvent>) {
    event.evt.preventDefault();
    if (event.evt.which === 3) {
      this.stage.startDrag();
      return;
    }
    if (event.target !== this.stage || this._selectionMode === SelectionType.Drag) {
      return;
    }
    const mousePointer = this.getMousePointer();
    switch (this._selectionMode) {
      case SelectionType.Default: {
        this.selectionRectPoints = {
          x1: mousePointer?.x,
          x2: mousePointer?.x,
          y1: mousePointer?.y,
          y2: mousePointer?.y,
        };
        this.selectionRect.visible(true);
        this.selectionRect.width(0);
        this.selectionRect.height(0);
        break;
      }
      case SelectionType.Lasso: {
        if (this.selectionShapesTimeoutId) {
          clearTimeout(this.selectionShapesTimeoutId);
          this.resetLasso();
          this.selectionShapesTimeoutId = null;
        }
        this.isLassoDrawingInProgress = true;
        this.createSelectionLasso([mousePointer.x, mousePointer.y]);
        break;
      }
    }
  }

  getMousePointer() {
    return this.utils.getPointerPosition(this.stage);
  }

  private onStageContextMenu(event: Konva.KonvaEventObject<MouseEvent>) {
    event.evt.preventDefault();
    const { data, pointerPosition } = this.utils.getSeatByPosition(event.target);
    if (data) {
      this._seatDetailsRequested$.next({
        id: data?.seatId,
        pointerPosition,
      });
    }
  }

  public fitStageIntoParentContainer() {
    if (this.stage) {
      this.stage.width(0);
      this.stage.height(0);
      setTimeout(() => {
        this._initialScale = this.utils.getInitialScale(this.container, this.canvasProps);
        this._maxScaleValue = this._initialScale * BlockViewScaleByFactor * 8;
        this.stage.width(this.container?.nativeElement?.offsetWidth);
        this.stage.height(this.container?.nativeElement?.offsetHeight);
        this.stage.scale({ x: this._initialScale, y: this._initialScale });
        this._currentScale$.next(this._initialScale);
        this.stage.position({ x: 0, y: 0 });
        setTimeout(() => {
          if (this.isShowMiniMap && this.minimap.minimapStage) {
            this.minimap.updateScaleRectOnZoom(this.stage.scaleX(), this.stage.position());
            this.minimap.rescaleMinimap();
          }
        }, 100);
      });
    }
  }

  private followMinimapScaleRect() {
    if (this.isShowMiniMap) {
      this.minimap.scaleRectPosition$.subscribe({
        next: (position: Vector2d) => {
          this.stage.setPosition(position);
        },
      });
    }
  }

  public applyZoom(wheelEvent: Konva.KonvaEventObject<WheelEvent>, zoomDirection?: number) {
    if (!this._isPlanDrawingInProgress) {
      const { oldScale, newScale, position } = this.utils.getZoomProps(
        this.stage,
        wheelEvent,
        BlockViewScaleByFactor,
        this._initialScale,
        zoomDirection,
      );
      if (newScale <= this._maxScaleValue && newScale >= this._initialScale && newScale !== oldScale) {
        this.stage.scale({ x: newScale, y: newScale });
        this.stage.position(position);
        this._currentScale$.next(newScale);
        if (this.isShowMiniMap) {
          this.minimap.toggleMinimapView(newScale === this._initialScale);
          this.minimap.updateScaleRectOnZoom(newScale, position);
        }
      }
    }
  }

  public resetZoom() {
    this.stage.scale({ x: this._initialScale, y: this._initialScale });
    this.stage.position({ x: 0, y: 0 });
    this._currentScale$.next(this._initialScale);
  }

  private createSelectionRect() {
    this.selectionRect = new Konva.Rect({
      fill: ChartsUtils.getColorByPropertyName('--color-scale-rect-bgrd'),
      stroke: ChartsUtils.getColorByPropertyName('--color-scale-rect-border'),
      strokeWidth: 1,
      visible: false,
    });
    this.selectionLayer.add(this.selectionRect);
  }

  private createSelectionLasso(points: [x: number, y: number]) {
    this.resetLasso();
    this.selectionLasso = new Konva.Line({
      id: 'selection-lasso',
      points,
      fill: ChartsUtils.getColorByPropertyName('--color-scale-rect-bgrd'),
      stroke: ChartsUtils.getColorByPropertyName('--color-scale-rect-border'),
      strokeWidth: 1,
      visible: true,
      closed: true,
      bezier: true,
    });
    this.selectionLayer.add(this.selectionLasso);
  }

  private resetLasso() {
    if (this.selectionLasso) {
      this.selectionLasso.destroy();
      this.selectionLasso?.points([]);
    }
  }

  public setSelectionMode(selection: SelectionType) {
    if (this._selectionMode !== selection) {
      this._selectionMode = selection;
      if (this.stage) {
        this.stage.setDraggable(selection === SelectionType.Drag);
      }
    }
  }

  private createSelectionTooltip() {
    this.selectionTooltip = this.utils.drawSelectionTooltip(
      this.translateService.instant('catalogPlan.selectionTtpText', { seatCount: 0 }),
    );
    this.selectionLayer.add(this.selectionTooltip);
  }

  private updateTooltipPositionAndValue() {
    this.selectionTooltipChanged$
      .pipe(
        takeUntil(this.destroy$),
        filter(() => {
          const isTooltipVisible = this.selectionTooltip.visible();
          if (!isTooltipVisible) {
            this.selectionTooltip
              .getText()
              .text(this.translateService.instant('catalogPlan.selectionTtpText', { seatCount: 0 }));
          }
          return isTooltipVisible;
        }),
        tap(() => {
          const { x, y } = this.stage.getPointerPosition();
          this.selectionTooltip.setAttrs({
            x: (x + 20 - this.stage.x()) / this.stage.scaleX(),
            y: (y + 5 - this.stage.y()) / this.stage.scaleY(),
            scale: { x: 1 / this.stage.scaleX(), y: 1 / this.stage.scaleY() },
          });
        }),
        debounceTime(100),
      )
      .subscribe({
        next: () => {
          const seats = this.utils.getAreaAndStageIntersection(
            this.contingentLayer,
            this._selectionMode === SelectionType.Default ? this.selectionRect : this.selectionLasso,
          );
          this.selectionTooltip
            .getText()
            .text(
              this.translateService.instant('catalogPlan.selectionTtpText', { seatCount: seats?.length }),
            );
        },
      });
  }

  private resetSelectionTooltip() {
    this.selectionTooltip.visible(false);
    this.selectionTooltip
      .getText()
      .text(this.translateService.instant('catalogPlan.selectionTtpText', { seatCount: 0 }));
  }

  private displaySelectionTooltip(event: MouseEvent) {
    if (!this.selectionTooltip.visible() && event?.button === 0) {
      this.selectionTooltip.visible(true);
    }
    this.selectionTooltipChanged$.next();
  }

  public destroy() {
    this.destroy$.next();
  }
}
